import React from 'react';
import { ScrollView } from 'react-native';

import { Box, Button, HStack, Heading, VStack, theme } from '@squire/ui';

import { CloseButton } from '@/components/_shared/CloseButton';
import { Page } from '@/components/_shared/Page';
import { SpinnerInline } from '@/components/_shared/SpinnerInline';
import { ChooseTimeCalendar } from '@/components/booking/ChooseTimeCalendar';
import { ChooseTimeGuestSection } from '@/components/booking/ChooseTimeGuestSection';
import { ChooseTimeLoadingScreen } from '@/components/booking/ChooseTimeLoadingScreen';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import type { CartItem } from '@/types/booking';

import { styles } from './styles';
import type { RescheduleSchedulePickerProps } from './types';

function RescheduleSchedulePicker({
  title,
  isScheduleLoading,
  isMutationPending,
  scheduleQuery,
  availabilityMap,
  end,
  rangeEnd,
  monthsAhead,
  today,
  timezone,
  onMonthChange,
  selectedDate,
  currentMonth,
  cartItem,
  onDateSelect,
  onTimeSelect,
  onReschedule,
  onNextAvailable,
  onClose,
  submitButtonLabel,
  isSubmitDisabled,
  loadingTestID = 'reschedule-loading',
}: RescheduleSchedulePickerProps) {
  const { t: tShared } = useScreenTranslations('shared');

  return (
    <Page scrollable={false} edges={['left', 'right']}>
      <VStack flex paddingVertical={theme.spacing[8]} space={theme.spacing[6]}>
        {/* Header */}
        <HStack paddingHorizontal={theme.spacing[5]} style={styles.header}>
          <Box flex>
            <Heading variant="s">{title}</Heading>
          </Box>

          {(isScheduleLoading || isMutationPending) && <SpinnerInline />}

          <CloseButton
            onPress={onClose}
            accessibilityLabel={tShared(
              'screenHeader.closeButton.accessibilityLabel',
            )}
            accessibilityHint={tShared(
              'screenHeader.closeButton.accessibilityHint',
            )}
          />
        </HStack>

        {/* Scrollable Content */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <VStack space={theme.spacing[8]}>
            {/* Calendar */}
            <ChooseTimeCalendar
              selectedDate={selectedDate}
              currentMonth={currentMonth}
              availabilityMap={availabilityMap}
              end={end}
              rangeEnd={rangeEnd}
              monthsAhead={monthsAhead}
              today={today}
              onDateSelect={onDateSelect}
              onMonthChange={onMonthChange}
            />

            {/* Time Slots */}
            {isScheduleLoading ? (
              <ChooseTimeLoadingScreen testID={loadingTestID} />
            ) : (
              <ChooseTimeGuestSection
                index={0}
                cartItem={cartItem as unknown as CartItem}
                timezone={timezone}
                isSingleAppointment={true}
                guestSchedule={{
                  guestId: 'guest-1',
                  data: scheduleQuery.data,
                  isLoading: scheduleQuery.isLoading,
                  isFetching: scheduleQuery.isFetching,
                  isError: scheduleQuery.isError,
                  error: scheduleQuery.error,
                  refetch: scheduleQuery.refetch,
                  isRefetching: scheduleQuery.isRefetching,
                }}
                selectedDate={selectedDate}
                isExpanded={true}
                onTimeSelect={onTimeSelect}
                onToggleExpanded={() => {}}
                onNavigateToNextAvailable={onNextAvailable}
                showSuggestedTimes={true}
              />
            )}
          </VStack>
        </ScrollView>
      </VStack>

      {/* Footer */}
      <VStack style={styles.footer}>
        <Button
          variant="primary"
          onPress={onReschedule}
          disabled={isSubmitDisabled}
          loading={isMutationPending}
        >
          {submitButtonLabel}
        </Button>
      </VStack>
    </Page>
  );
}

RescheduleSchedulePicker.displayName = 'RescheduleSchedulePicker';
export { RescheduleSchedulePicker };
